import {
  ActionCommitmentStatus,
  ActionOperationType,
  ActionRef,
} from 'further-types/lp/action';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import { AllocationRule } from 'further-types/lp/allocations';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { z } from 'zod';
import { CalendarDay } from '../../../utils';
import { isMongoId, withPaginationParams } from '../../helpers';
import isCountryCode from '../../helpers/is-country-code';
import { IndividualTransaction } from '../transaction';
import { CreateAllocation } from '../allocations';

const BankAccountInput = z.object({
  accountName: z
    .string()
    .min(1, { message: 'Account description is required' }),
  bankName: z.string().min(1, { message: 'Bank name is required' }),
  accountCurrency: z.nativeEnum(CurrencyCode),
  iban: z.string().optional(),
  accountNumber: z.string().optional(),
  sortCode: z.string().optional(),
  swift: z.string().optional(),
  additionalBankCode: z.string().optional(),
  additionalPaymentInformation: z.string().optional(),
  customPaymentReference: z.string().optional(),
  beneficiaryName: z.string().optional(),
  accountAddress: z.preprocess(
    (val) => {
      if (val && typeof val === 'object') {
        const hasAnyValue = Object.values(val).some(
          (v) => v !== '' && v != null,
        );
        if (!hasAnyValue) return undefined;
      }
      return val;
    },
    z
      .object({
        address1: z.string().optional(),
        address2: z.string().optional(),
        city: z.string().optional(),
        postcode: z.string().optional(),
        country: z.string().optional(),
      })
      .optional(),
  ),
});

export const ListBanksInput = z.object({
  country: z
    .string()
    .min(1, { message: 'Please select a country' })
    .refine(isCountryCode, { message: 'Please provide a valid country code' }),
});

export const ListBankAccountOperationsInput = withPaginationParams(
  z.object({
    bankAccountId: z.string().refine(isMongoId(), {
      message: 'Please provide a valid bank account ID',
    }),
    status: z.nativeEnum(BankAccountOperationStatus).optional(),
    search: z.string().optional(),
    dateFrom: z.instanceof(CalendarDay).optional(),
    dateTo: z.instanceof(CalendarDay).optional(),
  }),
  ['date'],
);

export const AuthoriseBankInput = z.object({
  institutionId: z.string().min(1, { message: 'Please select a bank' }),
  redirectUrl: z
    .string()
    .min(1, { message: 'Please enter a redirect URL' })
    .url({ message: 'Please enter a valid redirect URL' }),
});

export const GetAvailableBankAccountsInput = z.object({
  requisitionId: z.string().min(1, { message: 'Missing requisition ID' }),
});

export const ImportOperationsInput = z.object({
  bankAccountId: z.string().refine(isMongoId()),
});

export const DeleteBankAccountOperationsInput = z.object({
  bankAccountId: z.string().refine(isMongoId()),
  operationIds: z.array(z.string().refine(isMongoId())),
});

export const AddBankAccountInput = z.object({
  bankAccount: BankAccountInput,
});

export const UpdateBankAccountInput = z.object({
  bankAccount: BankAccountInput.extend({
    id: z.string().refine(isMongoId()),
  }),
});

export const AddBankAccountsFromBankFeedInput = z.object({
  bankAccountId: z
    .string()
    .optional()
    .nullable()
    .refine(isMongoId({ optional: true })),
  accounts: z.array(
    BankAccountInput.extend({
      bankFeedAccountId: z
        .string()
        .min(1, { message: 'Missing bank feed account ID' }),
      bankFeedInstitutionId: z
        .string()
        .min(1, { message: 'Missing bank feed institution ID' }),
      bankFeedInstitutionLogo: z.string().optional(),
      bankFeedAccessValidTo: z.date(),
    }),
  ),
});

export const GetMostRecentOperationInput = z.object({
  bankAccountId: z.string().refine(isMongoId()),
});

export const GetBankStatementPreviewInput = withPaginationParams(
  z.object({
    bankAccountId: z.string().refine(isMongoId()),
    dateFormat: z.string().min(1, { message: 'Date format is required.' }),
    bankStatementFilePath: z
      .string()
      .min(1, { message: 'Bank statement file is required.' }),
  }),
  ['date'],
);

export const ImportBankStatementInput = z.object({
  bankAccountId: z.string().refine(isMongoId()),
  dateFormat: z.string().min(1, { message: 'Date format is required.' }),
  bankStatementFilePath: z
    .string()
    .min(1, { message: 'Bank statement file is required.' }),
});

export const BankFeeFormInput = z.object({
  amountTcy: z.number().positive('Amount must be greater than 0.'),
  vatRate: z.number().min(0).max(100),
  description: z.string().optional(),
  allocationRule: z.nativeEnum(AllocationRule),
  allocations: z.array(CreateAllocation),
  currencyRate: z
    .object({
      fetchedRate: z.number().optional(),
      fetchedSource: z.nativeEnum(FxDataFeed).optional(),
      overrideRate: z.number().optional(),
      overrideSource: z.string().optional(),
    })
    .optional(),
  customActionTypeName: z.string().nullable().optional(),
  customActionTypeId: z
    .string()
    .nullable()
    .refine(isMongoId(), { message: 'Expense type is required.' }),
  fundingSource: z.nativeEnum(FundingSource, {
    message: 'Funding source is required.',
  }),
  commitmentStatus: z.nativeEnum(ActionCommitmentStatus, {
    message: 'Inside/outside commitment is required.',
  }),
  counterparty: z.object({
    id: z.string().optional(),
    name: z.string().min(1, { message: 'Counterparty is required.' }),
  }),
});

export const ReconcileFormSchema = z.object({
  bankFee: BankFeeFormInput.optional(),
});

export type ReconcileFormValues = z.infer<typeof ReconcileFormSchema>;

export const ReconcileBankAccountOperationInput = z.object({
  bankAccountId: z.string().refine(isMongoId()),
  bankAccountOperationId: z.string().refine(isMongoId()),
  reconciledTransactions: z.array(
    z.object({
      id: z.string().refine(isMongoId()),
      selectedAmount: z.number(),
      operationType: z.nativeEnum(ActionOperationType),
      currencyCode: z.nativeEnum(CurrencyCode),
      actionRef: z.nativeEnum(ActionRef),
    }),
  ),
  transactions: z
    .array(
      IndividualTransaction.extend({
        reconcilableTransactionId: z
          .string()
          .refine(isMongoId({ optional: true }))
          .optional(),
        isRevaluationGainTransaction: z.boolean().optional(),
        isUnrealisedGainOrLoss: z.boolean().optional(),
        amountExVatLcy: z.number(),
        amountIncVatLcy: z.number(),
      }),
    )
    .optional(),
  unrealisedCashFxGainUnwindTransactions: z
    .array(
      IndividualTransaction.extend({
        amountExVatLcy: z.number(),
        amountIncVatLcy: z.number(),
      }),
    )
    .optional(),
  fxRate: z
    .object({
      fetchedRate: z.number().optional(),
      fetchedSource: z.nativeEnum(FxDataFeed).optional(),
      overrideRate: z.number().optional(),
      overrideSource: z.string().optional(),
    })
    .optional(),
  bankFee: z
    .object({
      amountTcy: z.number().positive('Amount must be greater than 0'),
      vatRate: z.number().min(0).max(100),
      description: z.string().optional(),
      allocationRule: z.nativeEnum(AllocationRule),
      allocations: z.array(CreateAllocation),
      currencyRate: z
        .object({
          fetchedRate: z.number().optional(),
          fetchedSource: z.nativeEnum(FxDataFeed).optional(),
          overrideRate: z.number().optional(),
          overrideSource: z.string().optional(),
        })
        .optional(),
      customActionTypeId: z.string(),
      fundingSource: z.nativeEnum(FundingSource),
      commitmentStatus: z.nativeEnum(ActionCommitmentStatus),
      counterparty: z.object({
        id: z.string().optional(),
        name: z.string().min(1),
      }),
      expenseTransactions: z.array(
        IndividualTransaction.extend({
          amountExVatLcy: z.number(),
          amountIncVatLcy: z.number(),
        }),
      ),
      reconciliationTransactions: z.array(
        IndividualTransaction.extend({
          amountExVatLcy: z.number(),
          amountIncVatLcy: z.number(),
        }),
      ),
    })
    .optional(),
});

export const CreateBankAccountTransferInput = z.object({
  sourceAccountId: z.string().refine(isMongoId()),
  destinationAccountId: z.string().refine(isMongoId()),
  sourceBankOperationId: z.string().refine(isMongoId()),
  reference: z.string().optional(),
  transactions: z.array(IndividualTransaction),
  currencyRate: z
    .object({
      fetchedRate: z.number().optional(),
      fetchedSource: z.nativeEnum(FxDataFeed).optional(),
      overrideRate: z.number().optional(),
      overrideSource: z.string().optional(),
    })
    .optional(),
});

export const GetPrepaymentsInput = withPaginationParams(
  z.object({
    showUnapprovedOnly: z.boolean().optional(),
    viewDate: z.instanceof(CalendarDay).optional(),
    periodStart: z.instanceof(CalendarDay).optional(),
    periodEnd: z.instanceof(CalendarDay).optional(),
    actionOperationType: z.nativeEnum(ActionOperationType),
  }),
  ['ledgerDate'],
);

export const GetAccrualsInput = withPaginationParams(
  z.object({
    viewDate: z.instanceof(CalendarDay).optional(),
    periodStart: z.instanceof(CalendarDay).optional(),
    periodEnd: z.instanceof(CalendarDay).optional(),
    actionOperationType: z.nativeEnum(ActionOperationType),
  }),
  ['transactionDate'],
);

export const GetUnrealisedFXGainsInput = withPaginationParams(
  z.object({
    viewDate: z.instanceof(CalendarDay),
    currencyRateOverrides: z
      .record(
        z.nativeEnum(CurrencyCode),
        z.object({
          overrideRate: z.number().optional(),
          overrideSource: z.string().optional(),
        }),
      )
      .optional(),
  }),
  ['ledgerDate'],
);
